import type { DateTimeBounds } from '@shared/config'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { getFirstQueryValue } from './route'

dayjs.extend(utc)

const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/

/**
 * A `YYYY-MM-DD HH:mm:ss` bound, or `null` for anything the API would reject (including `2026-02-30`).
 * Checked in UTC so a wall-clock time inside a local DST gap is not mistaken for an invalid one.
 */
export const toDateTimeQueryValue = (value: unknown): string | null => {
  const raw = getFirstQueryValue(value)

  return raw !== null && DATE_TIME_PATTERN.test(raw) && dayjs.utc(raw).format(DateFormat.Default) === raw ? raw : null
}

/** The query's bounds; a lone `to` is dropped, since the date-range picker cannot show an end without a start. */
export const queryToDateTimeBounds = (fromValue: unknown, toValue: unknown): DateTimeBounds => {
  const from = toDateTimeQueryValue(fromValue)

  return {
    from,
    to: from ? toDateTimeQueryValue(toValue) : null,
  }
}

/** The date-range picker value for the stored bounds. */
export const toDateRangePickerValue = ({ from, to }: DateTimeBounds): Date[] | null => {
  const fromDate = from ? dayjs(from) : null
  const toDate = to ? dayjs(to) : null

  if (!fromDate?.isValid()) return null

  return toDate?.isValid() ? [fromDate.toDate(), toDate.toDate()] : [fromDate.toDate()]
}

/** The picker may emit a partial range (a start only); each bound applies on its own. */
export const fromDateRangePickerValue = (value: (Date | null | undefined)[] | null): DateTimeBounds => {
  const toBound = (date: Date | null | undefined): string | null =>
    date && dayjs(date).isValid() ? dayjs(date).format(DateFormat.Default) : null

  const from = toBound(value?.[0])

  return {
    from,
    to: from ? toBound(value?.[1]) : null,
  }
}
